import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { echo } from '@/shared/echo'
import { fetchGame, isGone } from '@/shared/game/api'
import { isLocalGame } from '@/shared/local/store'

/**
 * Состояние партии.
 *
 * Событие с канала не несёт состояние — только отметку «изменилось»: целиком
 * оно в сообщение не влезает, у Reverb предел 10 КБ (см. App\Events\GameUpdated).
 * Поэтому по событию партия перезапрашивается. Заодно ответ приходит
 * персональным: с полем «вы» и своими вариантами хода, чего в общей рассылке
 * быть не может.
 *
 * Сверху — редкий опрос как страховка: сообщение может потеряться (сокет
 * лёг, вкладка спала), и партия не должна оставаться замершей навсегда.
 */
export function gameKey(gameId: string): [string, string] {
  return ['game', gameId]
}

export function useGame(gameId: string) {
  const client = useQueryClient()
  // Партии за одним устройством ждать нечего: она меняется только от
  // нажатий на этом же экране, и канала у неё нет.
  const local = isLocalGame(gameId)

  const query = useQuery({
    queryKey: gameKey(gameId),
    queryFn: () => fetchGame(gameId),
    // Возврат на вкладку: пока она была неактивной, сокет мог отвалиться,
    // а события канала не хранятся и задним числом не приходят.
    refetchOnWindowFocus: 'always',
    staleTime: Number.POSITIVE_INFINITY,
    // Страховка от потерянного события. Редко: основной способ узнать
    // новости — всё-таки канал.
    refetchInterval: (query) => (!local && query.state.data?.status === 'playing' ? 15_000 : false),
    /*
     * 404 повторять нечего: партии по этому адресу нет и не появится — её
     * убрала уборка или адрес набран неверно. Три повтора здесь стоили
     * человеку трёх секунд ожидания перед сообщением, которое известно
     * сразу. Остальные ошибки повторяются как обычно: связь возвращается.
     */
    retry: (count, error) => !isGone(error) && count < 3,
  })

  useEffect(() => {
    if (local) return

    const channel = echo.channel(`games.${gameId}`)

    channel.listen('.game.updated', () => {
      void client.invalidateQueries({ queryKey: gameKey(gameId) })
    })

    return () => {
      echo.leaveChannel(`games.${gameId}`)
    }
  }, [gameId, client, local])

  return query
}
